// src/pages/payments/components/PaymentReasonModal.jsx
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './PaymentReasonModal.module.css';

/**
 * Generic reason-prompt dialog reused for Void, Flag as Issue, and Resolve actions
 * on a payment record. `required` controls whether the reason/note is mandatory.
 *
 * Props:
 *  isOpen        boolean
 *  onClose       () => void
 *  onConfirm     (reasonText) => void
 *  title         string
 *  description   string
 *  label         string — textarea label
 *  confirmLabel  string
 *  required      boolean (default true)
 *  isLoading     boolean
 */
const PaymentReasonModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  label,
  confirmLabel,
  required = true,
  isLoading = false
}) => {
  const { t } = useLocalization();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmed = reason.trim();
  const canConfirm = !required || trimmed.length >= 3;

  const handleConfirm = () => {
    if (!canConfirm || isLoading) return;
    onConfirm(trimmed);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.warningIcon}>
              <AlertTriangle size={20} />
            </div>
            <h3 className={styles.title}>{title}</h3>
          </div>
          <button className={styles.closeButton} onClick={onClose} disabled={isLoading} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className={styles.body}>
          {description && <p className={styles.description}>{description}</p>}

          <div className={styles.inputSection}>
            <label className={styles.inputLabel}>
              {label}{required && <strong> *</strong>}
            </label>
            <textarea
              className={styles.textarea}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
              rows={4}
              maxLength={1000}
              autoFocus
            />
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </button>
          <button className={styles.confirmButton} onClick={handleConfirm} disabled={!canConfirm || isLoading}>
            {isLoading && <span className={styles.spinner} />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentReasonModal;
